import { Link } from 'react-router';

import { Card, StatusPill, cn, focusRing } from '@/design-system';
import {
    MEMBERSHIP_STATUS_LABELS,
    MEMBERSHIP_STATUS_TONES,
    type PlatformUser,
} from '@/entities/user';
import { ROLE_LABELS } from '@/shared/api';
import { formatDayShort } from '@/shared/lib';
import { CardTitleRow } from '@/shared/ui';

import { paths } from '@/app/router/paths';
import { adminCopy } from '../copy';

export interface TenantMembersCardProps {
    tenantUuid: string;
    tenantName: string;
    users: readonly PlatformUser[];
}

/**
 * Wie er bij deze organisatie werkt, met rol en status van **dit** lidmaatschap.
 * Een naam opent die persoon, met al zijn andere bedrijven erbij.
 */
export function TenantMembersCard({ tenantUuid, tenantName, users }: TenantMembersCardProps) {
    return (
        <Card clip as="section" aria-labelledby="tenant-members-title">
            <CardTitleRow title={<span id="tenant-members-title">{adminCopy.membersTitle}</span>} />

            {users.length === 0 ? (
                <p className="m-0 px-4 py-[13px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {adminCopy.membersEmpty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0" aria-label={adminCopy.membersLabel(tenantName)}>
                    {users.map((user) => {
                        const membership = user.memberships.find(
                            (entry) => entry.tenantUuid === tenantUuid,
                        );

                        return (
                            <li
                                key={user.uuid}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--border-subtle-2)] px-4 py-[10px]"
                            >
                                <div className="min-w-[180px] flex-1">
                                    <Link
                                        to={paths.adminUser(user.uuid)}
                                        className={cn(
                                            'rounded-[2px] text-[13px] font-medium text-[var(--text-primary)] underline-offset-2 hover:text-[var(--accent)] hover:underline',
                                            focusRing,
                                        )}
                                    >
                                        {user.name}
                                    </Link>
                                    <div className="truncate text-[12px] text-[var(--text-tertiary-safe)]">
                                        {user.email}
                                    </div>
                                </div>
                                {membership ? (
                                    <>
                                        <span className="text-[12px] text-[var(--text-secondary)]">
                                            {membership.role
                                                ? ROLE_LABELS[membership.role]
                                                : adminCopy.unknownRole}
                                        </span>
                                        <StatusPill
                                            tone={MEMBERSHIP_STATUS_TONES[membership.status]}
                                            size="sm"
                                        >
                                            {MEMBERSHIP_STATUS_LABELS[membership.status]}
                                        </StatusPill>
                                        <span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-tertiary-safe)]">
                                            {membership.joinedAt
                                                ? adminCopy.memberSince(
                                                      formatDayShort(membership.joinedAt),
                                                  )
                                                : adminCopy.unknown}
                                        </span>
                                    </>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
}
